import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmationDialogue } from '../../providers/ConfirmationDialogue.provider';

import type { PortalPartnerLoginResponse } from '../useApi/usePostPortalPartnerLogin';

const usePartnerLoginDialogue = (response?: PortalPartnerLoginResponse): { showDialogue: () => void } => {
  const errorMessage: string = response?.errorMessage || '';

  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UsePartnerLoginDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { provideProps, showDialogue } = useConfirmationDialogue();

  // Keys are the raw backend error messages — match values, never translate them.
  const messageMap: { [backendErrorMessage: string]: string } = {
    'Required parameter(s) missing: Username': t('Errors.missingUsername', {
      defaultValue: 'Please provide a username.',
    }),
    'Required parameter(s) missing: Password': t('Errors.missingPassword', {
      defaultValue: 'Please provide a password.',
    }),
    'Required parameter(s) missing: Username, Password': t('Errors.missingUsernameAndPassword', {
      defaultValue: 'Please provide a username and password.',
    }),
    'Invalid Username!': t('Errors.invalidUsername', { defaultValue: 'An invalid username was provided.' }),
    'Invalid Password!': t('Errors.invalidPassword', { defaultValue: 'An invalid password was provided.' }),
  };

  useEffect(() => {
    const handlers = {
      errorMessage: () => {
        provideProps({
          title: t('errorTitle', {
            defaultValue: 'Unsuccessful login! There is a problem with your login credentials.',
          }),
          subTitle: messageMap[errorMessage] || errorMessage,
          status: 'error',
          confirm: {
            label: tGlobal('Buttons.stayHereAndTryAgain'),
            action: () => {},
          },
        });
      },
    };

    if (errorMessage) return handlers.errorMessage();

    return handlers.errorMessage();
  }, [errorMessage]);

  return { showDialogue };
};

export default usePartnerLoginDialogue;
